import { useState, useEffect, useRef, useCallback } from 'react';
import { api, proxyImageUrl, InfluencerCatalogProduct, InfluencerCatalogProductDetail, CreateProductRequestData } from '../api';

// ── Modal ─────────────────────────────────────────────────────────────────────

function Modal({
  isOpen, onClose, title, children, size = 'md',
}: {
  isOpen: boolean; onClose: () => void; title: string;
  children: React.ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  if (!isOpen) return null;
  const sizeClasses = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className={`card p-6 w-full ${sizeClasses[size]} relative z-10 animate-slide-up max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-bold text-ink-50">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Request modal (shared) ─────────────────────────────────────────────────────

function RequestModal({
  isOpen, onClose, product,
}: {
  isOpen: boolean; onClose: () => void;
  product: InfluencerCatalogProductDetail | null;
}) {
  const [form, setForm] = useState<CreateProductRequestData>({ vendor_catalog_product_id_fk: 0, wants_designer: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({ vendor_catalog_product_id_fk: product.id, wants_designer: 0, requested_retail: product.suggested_retail });
      setError(''); setSuccess(false);
    }
  }, [product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api.createProductRequest(form);
      setSuccess(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Request Product">
      {success ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-mint-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-mint-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-ink-100">Request Submitted!</h3>
          <p className="text-ink-400 mt-2">We'll review your request and get back to you soon.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-3 text-accent-400 text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Requested Retail Price</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">$</span>
              <input type="number" step="0.01" min={product?.min_retail} className="input w-full pl-8"
                value={form.requested_retail || ''} onChange={e => setForm({ ...form, requested_retail: parseFloat(e.target.value) })} />
            </div>
            {product && <p className="text-xs text-ink-500 mt-1">Minimum: {fmt(product.min_retail)}</p>}
          </div>
          {(product?.customizable || product?.printing_available || product?.embroidery_available) && (
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Customization Details</label>
              <textarea className="input w-full" rows={3} value={form.requested_customization || ''}
                onChange={e => setForm({ ...form, requested_customization: e.target.value })}
                placeholder="Colors, placement, size preferences…" />
            </div>
          )}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!form.wants_designer}
              onChange={e => setForm({ ...form, wants_designer: e.target.checked ? 1 : 0 })}
              className="w-5 h-5 rounded border-ink-600 text-accent-500 focus:ring-accent-500" />
            <span className="text-ink-200">I need help with design</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Design Assets URL (optional)</label>
            <input type="url" className="input w-full" value={form.design_assets_url || ''}
              onChange={e => setForm({ ...form, design_assets_url: e.target.value })}
              placeholder="https://drive.google.com/…" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Notes (optional)</label>
            <textarea className="input w-full" rows={2} value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes…" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary">{loading ? 'Submitting…' : 'Submit Request'}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ── Intel (Alibaba) Discover Tab ───────────────────────────────────────────────

type IntelProduct = {
  id: number; title: string; short_description: string | null;
  primary_image_url: string | null; price_min: number; price_max: number;
  currency: string; vertical: string; category: string;
  subcategory: string | null; tags: string[];
};

const PAGE_SIZE = 24;

function DiscoverTab() {
  const [products, setProducts] = useState<IntelProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [verticals, setVerticals] = useState<string[]>([]);
  const [filterVertical, setFilterVertical] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [imgErrors, setImgErrors] = useState<Set<number>>(new Set());

  // Detail / request modal
  const [selected, setSelected] = useState<IntelProduct | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeqRef = useRef(0);

  // Load verticals once
  useEffect(() => {
    api.getIntelProductVerticals().then(({ verticals }) => setVerticals(verticals)).catch(() => {});
  }, []);

  const load = useCallback(async (newOffset: number, newSearch: string, newVertical: string) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const { products, total } = await api.getIntelProducts({ search: newSearch, vertical: newVertical, limit: PAGE_SIZE, offset: newOffset });
      // Ignore out-of-order responses so a slow earlier request doesn't
      // overwrite a faster later one.
      if (seq !== requestSeqRef.current) return;
      setProducts(products);
      setTotal(total);
      setOffset(newOffset);
    } catch {
      if (seq === requestSeqRef.current) setProducts([]);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(0, search, filterVertical); }, [filterVertical]); // eslint-disable-line
  useEffect(() => { load(0, search, filterVertical); }, []); // eslint-disable-line

  const handleSearchInput = (val: string) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val); load(0, val, filterVertical); }, 400);
  };

  const fmt = (n: number) => n >= 100 ? `$${Math.round(n)}` : `$${n.toFixed(2)}`;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Search + filter bar */}
      <div className="card p-4 space-y-3">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input className="input w-full pl-9" placeholder="Search Alibaba products…"
            value={searchInput} onChange={e => handleSearchInput(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setFilterVertical(''); load(0, search, ''); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterVertical === '' ? 'bg-accent-500 text-ink-950' : 'bg-ink-800 text-ink-300 hover:bg-ink-700'}`}>
            All Categories
          </button>
          {verticals.slice(0, 10).map(v => (
            <button key={v} onClick={() => { setFilterVertical(v); load(0, search, v); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filterVertical === v ? 'bg-accent-500 text-ink-950' : 'bg-ink-800 text-ink-300 hover:bg-ink-700'}`}>
              {v}
            </button>
          ))}
        </div>
        <p className="text-xs text-ink-500">{total.toLocaleString()} products from Alibaba</p>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <div key={i} className="rounded-xl bg-ink-900 overflow-hidden animate-pulse">
              <div className="aspect-square bg-ink-800" />
              <div className="p-3 space-y-2">
                <div className="h-3 bg-ink-700 rounded w-3/4" />
                <div className="h-3 bg-ink-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="card p-12 text-center text-ink-400">No products found.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {products.map(p => (
            <div key={p.id} onClick={() => { setSelected(p); setShowDetail(true); }}
              className="group rounded-xl bg-ink-900 border border-ink-700/40 overflow-hidden cursor-pointer hover:border-accent-500/40 hover:shadow-lg hover:shadow-accent-500/5 transition-all">
              <div className="aspect-square bg-ink-800 overflow-hidden relative">
                {p.primary_image_url && !imgErrors.has(p.id) ? (
                  <img src={proxyImageUrl(p.primary_image_url)!} alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={() => setImgErrors(prev => new Set([...prev, p.id]))} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-10 h-10 text-ink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-xs font-medium text-ink-100 line-clamp-2 group-hover:text-accent-400 transition-colors leading-snug mb-1">
                  {p.title}
                </p>
                <p className="text-xs text-ink-500 capitalize">{p.vertical}</p>
                <p className="text-xs font-semibold text-accent-400 mt-1">{fmt(p.price_min)} – {fmt(p.price_max)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => load(offset - PAGE_SIZE, search, filterVertical)} disabled={currentPage === 0}
            className="btn-ghost px-3 py-2 text-sm disabled:opacity-40">← Prev</button>
          <span className="text-sm text-ink-400">Page {currentPage + 1} of {totalPages}</span>
          <button onClick={() => load(offset + PAGE_SIZE, search, filterVertical)} disabled={currentPage >= totalPages - 1}
            className="btn-ghost px-3 py-2 text-sm disabled:opacity-40">Next →</button>
        </div>
      )}

      {/* Detail modal */}
      <Modal isOpen={showDetail} onClose={() => setShowDetail(false)} title={selected?.title ?? 'Product Detail'} size="lg">
        {selected && (
          <div className="space-y-5">
            <div className="aspect-video bg-ink-800 rounded-xl overflow-hidden flex items-center justify-center">
              {selected.primary_image_url && !imgErrors.has(selected.id) ? (
                <img src={proxyImageUrl(selected.primary_image_url)!} alt={selected.title}
                  className="w-full h-full object-contain"
                  onError={() => setImgErrors(prev => new Set([...prev, selected.id]))} />
              ) : (
                <svg className="w-16 h-16 text-ink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            </div>
            <div>
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="text-xs bg-ink-700 text-ink-300 px-2 py-0.5 rounded capitalize">{selected.vertical}</span>
                <span className="text-xs bg-ink-700 text-ink-300 px-2 py-0.5 rounded capitalize">{selected.category}</span>
                {selected.subcategory && <span className="text-xs bg-ink-700 text-ink-300 px-2 py-0.5 rounded capitalize">{selected.subcategory}</span>}
              </div>
              <h3 className="text-lg font-bold text-ink-100">{selected.title}</h3>
              {selected.short_description && <p className="text-ink-400 text-sm mt-2">{selected.short_description}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4 p-4 bg-ink-800 rounded-xl">
              <div className="text-center">
                <div className="text-xs text-ink-500 uppercase tracking-wider">Price Range</div>
                <div className="text-xl font-bold text-accent-400 mt-1">{fmt(selected.price_min)} – {fmt(selected.price_max)}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-ink-500 uppercase tracking-wider">Source</div>
                <div className="text-lg font-bold text-ink-200 mt-1">Alibaba</div>
              </div>
            </div>
            {selected.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selected.tags.slice(0, 10).map(t => (
                  <span key={t} className="text-xs bg-ink-700/60 text-ink-400 px-2 py-0.5 rounded-full">{t}</span>
                ))}
              </div>
            )}
            <div className="p-3 bg-ink-800/50 rounded-xl border border-ink-700/50 text-sm text-ink-400">
              <svg className="w-4 h-4 inline mr-1.5 text-ink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              This product is sourced from Alibaba. Contact your account manager to discuss adding it to your storefront.
            </div>
            <button onClick={() => setShowDetail(false)} className="btn-primary w-full">Close</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Vendor Catalog Tab ─────────────────────────────────────────────────────────

const CATALOG_CATEGORIES = ['shirt','hoodie','sweatshirt','pants','shorts','jacket','hat','bag','accessory','beauty','other'];

function CatalogTab() {
  const [products, setProducts] = useState<InfluencerCatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<InfluencerCatalogProductDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showRequest, setShowRequest] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.browseCatalogProducts({ category: filterCategory || undefined })
      .then(({ products }) => setProducts(products))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load products'))
      .finally(() => setLoading(false));
  }, [filterCategory]);

  const openDetail = async (p: InfluencerCatalogProduct) => {
    setDetailLoading(true); setShowDetail(true);
    try {
      const { product } = await api.getCatalogProductDetail(p.id);
      setSelectedProduct(product);
    } catch {
      setError('Failed to load product');
    } finally {
      setDetailLoading(false);
    }
  };

  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  const filtered = products.filter(p =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {error && <div className="bg-accent-500/10 border border-accent-500/20 rounded-xl p-4 text-accent-400 text-sm">{error}</div>}

      <div className="card p-4 space-y-3">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input className="input w-full pl-9" placeholder="Search products or vendors…"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilterCategory('')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterCategory === '' ? 'bg-accent-500 text-ink-950' : 'bg-ink-800 text-ink-300 hover:bg-ink-700'}`}>
            All
          </button>
          {CATALOG_CATEGORIES.slice(0, 8).map(cat => (
            <button key={cat} onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filterCategory === cat ? 'bg-accent-500 text-ink-950' : 'bg-ink-800 text-ink-300 hover:bg-ink-700'}`}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-xl bg-ink-900 overflow-hidden animate-pulse">
              <div className="aspect-square bg-ink-800" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-ink-700 rounded w-3/4" />
                <div className="h-3 bg-ink-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-ink-400">No products in the vendor catalog yet.</p>
          <p className="text-ink-500 text-sm mt-2">Check the <strong>Discover</strong> tab to browse thousands of Alibaba products.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map(product => (
            <div key={product.id} onClick={() => openDetail(product)}
              className="card overflow-hidden cursor-pointer hover:ring-1 hover:ring-accent-500/30 transition-all group">
              <div className="aspect-square bg-ink-800 flex items-center justify-center relative overflow-hidden">
                <svg className="w-16 h-16 text-ink-600 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="p-4">
                <h3 className="font-medium text-ink-100 line-clamp-1 group-hover:text-accent-400 transition-colors">{product.title}</h3>
                <p className="text-sm text-ink-400">{product.vendor_name}</p>
                <div className="flex flex-wrap gap-1 my-2">
                  <span className="text-xs bg-ink-800 text-ink-300 px-2 py-0.5 rounded capitalize">{product.category}</span>
                  {product.customizable ? <span className="text-xs bg-accent-500/20 text-accent-400 px-2 py-0.5 rounded">Custom</span> : null}
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-lg font-bold text-accent-400">{fmt(product.min_retail)}</span>
                  <span className="text-xs text-ink-500">Suggested: {fmt(product.suggested_retail)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <Modal isOpen={showDetail} onClose={() => { setShowDetail(false); setSelectedProduct(null); }} title={selectedProduct?.title ?? 'Product Details'} size="lg">
        {detailLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
          </div>
        ) : selectedProduct ? (
          <div className="space-y-5">
            <div className="aspect-video bg-ink-800 rounded-xl flex items-center justify-center overflow-hidden">
              {selectedProduct.images.length > 0 ? (
                <img src={selectedProduct.images[0].image_url} alt={selectedProduct.images[0].alt_text || selectedProduct.title}
                  className="w-full h-full object-contain rounded-xl" />
              ) : (
                <svg className="w-16 h-16 text-ink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            </div>
            <div>
              <p className="text-ink-400 text-sm">{selectedProduct.vendor_name}</p>
              <h2 className="text-xl font-bold text-ink-100 mt-1">{selectedProduct.title}</h2>
              {selectedProduct.description && <p className="text-ink-300 mt-2 text-sm">{selectedProduct.description}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-sm bg-ink-800 text-ink-300 px-3 py-1 rounded-full capitalize">{selectedProduct.category}</span>
              {selectedProduct.customizable ? <span className="text-sm bg-accent-500/20 text-accent-400 px-3 py-1 rounded-full">Customizable</span> : null}
              {selectedProduct.printing_available ? <span className="text-sm bg-royal-500/20 text-royal-400 px-3 py-1 rounded-full">Printing Available</span> : null}
              {selectedProduct.embroidery_available ? <span className="text-sm bg-mint-500/20 text-mint-400 px-3 py-1 rounded-full">Embroidery Available</span> : null}
            </div>
            <div className="grid grid-cols-3 gap-4 p-4 bg-ink-800 rounded-xl">
              {[['Base Cost', selectedProduct.base_cost], ['Min Retail', selectedProduct.min_retail], ['Suggested', selectedProduct.suggested_retail]].map(([label, val]) => (
                <div key={label as string} className="text-center">
                  <div className="text-xs text-ink-500 uppercase tracking-wider">{label}</div>
                  <div className="text-base font-bold text-ink-200 mt-1">{fmt(val as number)}</div>
                </div>
              ))}
            </div>
            <p className="text-sm text-ink-400">Min order: {selectedProduct.moq} unit(s)</p>
            <button onClick={() => setShowRequest(true)} className="btn-primary w-full py-3 text-base">Request This Product</button>
          </div>
        ) : null}
      </Modal>

      <RequestModal isOpen={showRequest} onClose={() => { setShowRequest(false); setShowDetail(false); }} product={selectedProduct} />
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function InfluencerExplore() {
  const [activeTab, setActiveTab] = useState<'discover' | 'catalog'>('discover');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-ink-50">Explore Products</h1>
          <p className="text-ink-400 mt-1">Browse and request products for your storefront</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-ink-900 border border-ink-700/50 rounded-xl p-1 w-fit">
        <button onClick={() => setActiveTab('discover')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'discover' ? 'bg-accent-500 text-ink-950 shadow-sm' : 'text-ink-400 hover:text-ink-200'}`}>
          Discover
          <span className="ml-2 text-xs opacity-70">Alibaba</span>
        </button>
        <button onClick={() => setActiveTab('catalog')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'catalog' ? 'bg-accent-500 text-ink-950 shadow-sm' : 'text-ink-400 hover:text-ink-200'}`}>
          Vendor Catalog
        </button>
      </div>

      {activeTab === 'discover' ? <DiscoverTab /> : <CatalogTab />}
    </div>
  );
}
